import { useState } from "react";

import {
  AddOutlined,
  CheckOutlined,
  DeleteOutlined,
  EditOutlined,
} from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  EVOTRUX_BLUE,
  EVOTRUX_BORDER_GREY,
  EVOTRUX_GREEN,
  MAX_TFC_LINE_ITEM_DESCRIPTION_LENGTH,
  MAX_TFC_LINE_ITEMS,
} from "constants/index";
import { formatCentsToDollars, parseDollarsToCents } from "utils";

interface EditableLineItem {
  id: string;
  description: string;
  line_total_cents: number;
}

interface TfcEditableTableProps {
  bookedPriceCents: number | null;
  currency: string;
  lineItems: EditableLineItem[];
  onLineItemsChange: (items: EditableLineItem[]) => void;
}

export function TfcEditableTable(props: TfcEditableTableProps) {
  const { bookedPriceCents, currency, lineItems, onLineItemsChange } = props;

  const [editingId, setEditingId] = useState<string | null>(null);
  const [amountInput, setAmountInput] = useState("");
  const [nextId, setNextId] = useState(1);

  const totalCents =
    (bookedPriceCents ?? 0) +
    lineItems.reduce((sum, item) => sum + item.line_total_cents, 0);

  const isRowComplete = (item: EditableLineItem) =>
    item.description.trim() !== "" && item.line_total_cents !== 0;

  const isEditingRowValid = () => {
    if (!editingId) return true;
    const item = lineItems.find((i) => i.id === editingId);
    if (!item) return true;
    const cents = parseDollarsToCents(amountInput);
    return item.description.trim() !== "" && cents !== null && cents !== 0;
  };

  const commitAmount = (id: string) => {
    const cents = parseDollarsToCents(amountInput);
    if (cents === null) return;
    onLineItemsChange(
      lineItems.map((item) =>
        item.id === id ? { ...item, line_total_cents: cents } : item,
      ),
    );
  };

  const handleAdd = () => {
    if (lineItems.length >= MAX_TFC_LINE_ITEMS) return;
    if (editingId && !isEditingRowValid()) return;
    if (editingId) commitAmount(editingId);
    const id = `item-${nextId}`;
    setNextId(nextId + 1);
    onLineItemsChange([
      ...lineItems,
      { id, description: "", line_total_cents: 0 },
    ]);
    setEditingId(id);
    setAmountInput("");
  };

  const handleRemove = (id: string) => {
    onLineItemsChange(lineItems.filter((item) => item.id !== id));
    if (editingId === id) {
      setEditingId(null);
      setAmountInput("");
    }
  };

  const handleDescriptionChange = (id: string, value: string) => {
    onLineItemsChange(
      lineItems.map((item) =>
        item.id === id ? { ...item, description: value } : item,
      ),
    );
  };

  const handleConfirm = (id: string) => {
    if (!isEditingRowValid()) return;
    commitAmount(id);
    setEditingId(null);
    setAmountInput("");
  };

  const handleEdit = (id: string) => {
    if (editingId && editingId !== id) {
      if (isEditingRowValid()) commitAmount(editingId);
    }
    const item = lineItems.find((i) => i.id === id);
    setEditingId(id);
    setAmountInput(
      item && item.line_total_cents !== 0
        ? formatCentsToDollars(item.line_total_cents).replace(/,/g, "")
        : "",
    );
  };

  const hasIncompleteItem =
    editingId !== null ||
    lineItems.some((item) => !item.description || item.line_total_cents === 0);

  return (
    <>
      <Box
        sx={{
          border: `1px solid ${EVOTRUX_BORDER_GREY}`,
          borderRadius: "4px",
          overflow: "hidden",
          mb: "10px",
        }}
      >
        <TableContainer>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{ fontWeight: 700, fontSize: "16px", py: "5px" }}
                >
                  Description
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    fontSize: "16px",
                    py: "5px",
                    width: "150px",
                  }}
                >
                  Amount
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    fontSize: "16px",
                    py: "5px",
                    width: "70px",
                  }}
                >
                  Currency
                </TableCell>
                <TableCell sx={{ py: "5px", width: "80px" }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {bookedPriceCents != null && (
                <TableRow>
                  <TableCell sx={{ py: "5px" }}>
                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: "16px",
                        color: "#4B5563",
                      }}
                    >
                      Booked Price
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: "5px" }}>
                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: "16px",
                        color: "#4B5563",
                      }}
                    >
                      ${formatCentsToDollars(bookedPriceCents)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: "5px" }}>
                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: "16px",
                        color: "#4B5563",
                      }}
                    >
                      {currency}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: "5px" }} />
                </TableRow>
              )}

              {lineItems.map((item) => {
                const editing = editingId === item.id;
                const valid = editing
                  ? isEditingRowValid()
                  : isRowComplete(item);
                return (
                  <TableRow key={item.id}>
                    <TableCell sx={{ py: "5px" }}>
                      {editing ? (
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="e.g., 2 hours layover"
                          value={item.description}
                          onChange={(e) =>
                            handleDescriptionChange(item.id, e.target.value)
                          }
                          inputProps={{
                            maxLength: MAX_TFC_LINE_ITEM_DESCRIPTION_LENGTH,
                          }}
                        />
                      ) : (
                        <Typography
                          sx={{ fontSize: "16px", wordBreak: "break-word" }}
                        >
                          {item.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ py: "5px" }}>
                      {editing ? (
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="0.00"
                          value={amountInput}
                          onChange={(e) => {
                            const { value } = e.target;
                            if (
                              value === "" ||
                              /^-?\d*\.?\d{0,2}$/.test(value)
                            ) {
                              setAmountInput(value);
                            }
                          }}
                        />
                      ) : (
                        <Typography sx={{ fontSize: "16px" }}>
                          ${formatCentsToDollars(item.line_total_cents)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ py: "5px" }}>
                      <Typography sx={{ fontSize: "16px", color: "#4B5563" }}>
                        {currency}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: "5px" }}>
                      <Stack
                        direction="row"
                        spacing={0}
                        justifyContent="flex-end"
                      >
                        {editing ? (
                          <Tooltip
                            title={valid ? "Confirm" : "Fill in both fields"}
                          >
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleConfirm(item.id)}
                                disabled={!valid}
                                sx={{
                                  color: valid ? EVOTRUX_GREEN : "#9CA3AF",
                                }}
                              >
                                <CheckOutlined fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(item.id)}
                              sx={{ color: EVOTRUX_BLUE }}
                            >
                              <EditOutlined fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Remove">
                          <IconButton
                            size="small"
                            onClick={() => handleRemove(item.id)}
                            sx={{ color: "#D32F2F" }}
                          >
                            <DeleteOutlined fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}

              {lineItems.length < MAX_TFC_LINE_ITEMS && (
                <TableRow>
                  <TableCell colSpan={4} sx={{ py: "5px" }}>
                    <Button
                      startIcon={<AddOutlined />}
                      onClick={handleAdd}
                      disabled={hasIncompleteItem}
                      sx={{
                        color: EVOTRUX_BLUE,
                        textTransform: "none",
                        fontWeight: 500,
                        fontSize: "16px",
                        "&:hover": {
                          backgroundColor: "transparent",
                          textDecoration: "underline",
                        },
                        "&.Mui-disabled": { color: "#9CA3AF" },
                      }}
                    >
                      Add line item
                    </Button>
                    {lineItems.length > 0 && (
                      <Typography
                        component="span"
                        sx={{ ml: 2, fontSize: "16px", color: "#6B7280" }}
                      >
                        ({MAX_TFC_LINE_ITEMS - lineItems.length} remaining)
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              )}

              <TableRow>
                <TableCell sx={{ py: "5px", borderBottom: "none" }}>
                  <Typography sx={{ fontWeight: 700, fontSize: "16px" }}>
                    Total Freight Cost
                  </Typography>
                </TableCell>
                <TableCell sx={{ py: "5px", borderBottom: "none" }}>
                  <Typography sx={{ fontWeight: 700, fontSize: "16px" }}>
                    ${formatCentsToDollars(totalCents)}
                  </Typography>
                </TableCell>
                <TableCell sx={{ py: "5px", borderBottom: "none" }}>
                  <Typography sx={{ fontWeight: 700, fontSize: "16px" }}>
                    {currency}
                  </Typography>
                </TableCell>
                <TableCell sx={{ py: "5px", borderBottom: "none" }} />
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Typography sx={{ fontSize: "12px", color: "#6B7280", mb: 3 }}>
        <strong>Do not include tax</strong> in your line items, as this is not
        an invoice. Applicable taxes can/should be added at time of invoice.
      </Typography>
    </>
  );
}

export type { EditableLineItem };
